import axios from 'axios';

/**
 * Gemensam API-klient.
 *
 * Sessionen bestar av tva delar: en kortlivad atkomsttoken som skickas med
 * varje anrop, och en langlivad fornyelsetoken som byts in mot en ny nar
 * den forsta gatt ut. Det gor att man kan forbli inloggad i manader utan
 * att en lackt atkomsttoken ar anvandbar mer an ett dygn.
 */
const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
});

const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refreshToken';

let accessToken = localStorage.getItem(TOKEN_KEY);
let refreshToken = localStorage.getItem(REFRESH_KEY);
let onUnauthorized = null;
let onSessionChange = null;

export function setSession({ token, refreshToken: refresh }) {
  accessToken = token || null;
  if (refresh !== undefined) refreshToken = refresh || null;

  if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
  else localStorage.removeItem(TOKEN_KEY);

  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  else localStorage.removeItem(REFRESH_KEY);

  if (onSessionChange) onSessionChange(accessToken);
}

export function clearSession() {
  setSession({ token: null, refreshToken: null });
}

export const getAuthToken = () => accessToken;
export const getRefreshToken = () => refreshToken;

export function setUnauthorizedHandler(handler) { onUnauthorized = handler; }
export function setSessionChangeHandler(handler) { onSessionChange = handler; }

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * Hamtar en ny atkomsttoken. Flera samtidiga 401:or ska bara ge ett anrop,
 * darfor delas ett och samma lofte ut till alla som fragar.
 */
let pendingRefresh = null;

export function refreshSession() {
  if (!refreshToken) return Promise.reject(new Error('Ingen session att förnya'));
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = axios
    .post('/api/auth/refresh', { refreshToken })
    .then((res) => {
      setSession({ token: res.data.token, refreshToken: res.data.refreshToken });
      return res.data.token;
    })
    .catch((err) => {
      // Fornyelsen gick inte att gora — sessionen ar slut pa riktigt.
      clearSession();
      if (onUnauthorized) onUnauthorized();
      throw err;
    })
    .finally(() => { pendingRefresh = null; });

  return pendingRefresh;
}

const isAuthEndpoint = (url = '') => url.startsWith('/auth/') || url.includes('/api/auth/');

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const status = response && response.status;

    // Ett 401 pa ett vanligt anrop betyder oftast bara att atkomsttoken
    // gatt ut. Forsok forny en gang och kor om anropet.
    if (status === 401 && config && !config._retried && !isAuthEndpoint(config.url) && refreshToken) {
      config._retried = true;
      try {
        await refreshSession();
        return api(config);
      } catch {
        return Promise.reject(error);
      }
    }

    if (status === 401 && !isAuthEndpoint(config && config.url) && onUnauthorized) {
      onUnauthorized();
    }

    return Promise.reject(error);
  }
);

/**
 * Sakrar att ett svar som ska vara en lista ocksa ar det.
 *
 * En tom kropp — till exempel fran ett 304-svar som webblasaren inte fyllt
 * i — gav tidigare en strang dar komponenten vantade en array, och hela
 * vyn kraschade pa .map. En tom lista ar ett battre svar an en vit skarm.
 */
export const asArray = (value) => (Array.isArray(value) ? value : []);

/** Plockar ut serverns felmeddelande, med en begriplig reserv. */
export function errorMessage(error, fallback = 'Något gick fel. Försök igen.') {
  if (error && error.response && error.response.data && error.response.data.error) {
    return error.response.data.error;
  }
  if (error && error.code === 'ECONNABORTED') return 'Servern svarar inte. Kontrollera din uppkoppling.';
  if (error && !error.response) return 'Ingen kontakt med servern.';
  return fallback;
}

export default api;
